import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { useCurrency } from "@/lib/currency";
import { absUrl } from "@/components/PhotoUploader";
import { ShieldCheck, Loader2 } from "lucide-react";

// Terracotta-colored price tag marker via divIcon (with optional verified badge overlay)
function priceTagIcon(label, active = false, verified = false) {
  const bg = active ? "#231F1B" : "#C55A3C";
  const verifiedBadge = verified
    ? `<span title="Verified" style="
        position:absolute;top:-6px;right:-6px;
        width:16px;height:16px;border-radius:50%;
        background:#8AA88F;color:#fff;
        display:flex;align-items:center;justify-content:center;
        border:2px solid #fff;
        box-shadow:0 2px 6px -1px rgba(35,31,27,.35);
        font-size:9px;font-weight:900;line-height:1;
      ">✓</span>`
    : "";
  return L.divIcon({
    className: "hh-price-tag",
    html: `<div style="
      background:${bg};color:#fff;
      padding:6px 12px;border-radius:999px;
      font-family:Manrope,system-ui,sans-serif;
      font-size:12px;font-weight:700;letter-spacing:.02em;
      white-space:nowrap;
      box-shadow:0 4px 12px -2px rgba(35,31,27,.35);
      border:2px solid #fff;
      transform:translate(-50%,-100%);
      position:relative;
    ">${label}${verifiedBadge}
      <span style="
        position:absolute;left:50%;top:100%;
        transform:translate(-50%,-2px) rotate(45deg);
        width:8px;height:8px;background:${bg};
        border-right:2px solid #fff;border-bottom:2px solid #fff;
      "></span>
    </div>`,
    iconSize: [1, 1],
    iconAnchor: [0, 0],
  });
}

function FitBounds({ pins, enabled }) {
  const map = useMap();
  useEffect(() => {
    if (!enabled || !pins.length) return;
    const bounds = L.latLngBounds(pins.map((p) => [p.lat, p.lng]));
    map.fitBounds(bounds.pad(0.15), { animate: true });
  }, [pins, map, enabled]);
  return null;
}

function BoundsWatcher({ onMove }) {
  useMapEvents({
    moveend: (e) => onMove(e.target.getBounds()),
    zoomend: (e) => onMove(e.target.getBounds()),
  });
  return null;
}

export default function PropertyMap() {
  const { t, lang } = useLang();
  const { format } = useCurrency();
  const [pins, setPins] = useState([]);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const debounceRef = useRef(null);

  const fetchPins = async (bounds) => {
    setBusy(true);
    try {
      const params = bounds ? {
        sw_lat: bounds.getSouth(), sw_lng: bounds.getWest(),
        ne_lat: bounds.getNorth(), ne_lng: bounds.getEast(),
      } : {};
      const r = await api.get("/map/pins", { params });
      setPins(r.data.pins || []);
    } catch {}
    finally { setBusy(false); }
  };

  useEffect(() => { fetchPins(); }, []);

  const onMove = (bounds) => {
    if (firstLoad) { setFirstLoad(false); return; } // skip the auto-fit-triggered event
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPins(bounds), 350);
  };

  const italyCenter = [42.5, 12.5];

  return (
    <section id="map" className="bg-ink text-sand py-24 grain-bg" data-testid="map-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
          <div className="lg:col-span-7">
            <div className="uppercase tracking-[0.2em] text-xs text-primary font-semibold mb-4">— {lang === "it" ? "Sulla mappa" : "On the map"}</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1]">
              {lang === "it" ? "Una rete che copre" : "A network that spans"}<br/>
              <span className="italic text-primary">{lang === "it" ? "tutta l'Italia." : "all of Italy."}</span>
            </h2>
          </div>
          <div className="lg:col-span-5 text-sand/80 text-sm leading-relaxed">
            {lang === "it"
              ? "Dalla Costiera Amalfitana alle Dolomiti: strutture verificate, prezzi trasparenti, prenotazione diretta. Clicca su un cartellino per vedere la struttura."
              : "From the Amalfi Coast to the Dolomites: verified structures, transparent prices, direct booking. Click a tag to preview the property."}
          </div>
        </div>

        <div className="relative rounded-lg overflow-hidden border border-sand/10 shadow-2xl" style={{ height: "560px" }}>
          {busy && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-[500] bg-ink/90 text-sand text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full flex items-center gap-2 border border-sand/10 shadow-lg" data-testid="map-loading">
              <Loader2 className="w-3.5 h-3.5 animate-spin"/>
              {lang === "it" ? "Ricerca in quest'area..." : "Searching this area..."}
            </div>
          )}
          <MapContainer
            center={italyCenter}
            zoom={6}
            style={{ height: "100%", width: "100%", background: "#231F1B" }}
            scrollWheelZoom={false}
            attributionControl={true}
            className="hh-vintage-map"
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              maxZoom={19}
              subdomains={["a", "b", "c"]}
            />
            <FitBounds pins={pins} enabled={firstLoad}/>
            <BoundsWatcher onMove={onMove}/>
            {pins.map((p) => {
              const hasPrice = p.from_price != null && Number(p.from_price) > 0;
              const priceLabel = hasPrice ? format(p.from_price, { digits: 0 }) : (lang === "it" ? "Da —" : "From —");
              return (
              <Marker
                key={p.slug}
                position={[p.lat, p.lng]}
                icon={priceTagIcon(priceLabel, active === p.slug, !!p.verified)}
                eventHandlers={{ click: () => setActive(p.slug) }}
                data-testid={`pin-${p.slug}`}
              >
                <Popup className="hh-popup" minWidth={240}>
                  <div className="w-full">
                    {p.hero_image && (
                      <img src={absUrl(p.hero_image)} alt={p.name} className="w-full h-32 object-cover rounded-md mb-2"/>
                    )}
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      <span className="font-display text-lg leading-tight" style={{ color: "#231F1B" }}>{p.name}</span>
                      {p.verified && <ShieldCheck className="w-3.5 h-3.5" style={{ color: "#8AA88F" }}/>}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">{p.city}, {p.region}</div>
                    <div className="flex items-end justify-between">
                      <div>
                        {hasPrice ? (
                          <>
                            <div className="font-display text-xl" style={{ color: "#C55A3C" }}>{format(p.from_price, { digits: 0 })}</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">{lang === "it" ? "da / notte" : "from / night"}</div>
                          </>
                        ) : (
                          <div className="text-xs italic text-neutral-400" data-testid={`pin-noprice-${p.slug}`}>
                            {lang === "it" ? "Tariffa da definire" : "Rate not available"}
                          </div>
                        )}
                      </div>
                      {p.real ? (
                        <Link to={`/p/${p.slug}`} className="text-xs font-semibold hover:underline" style={{ color: "#C55A3C" }} data-testid={`pin-link-${p.slug}`}>
                          {lang === "it" ? "Scopri →" : "View →"}
                        </Link>
                      ) : (
                        <span className="text-[10px] italic text-neutral-400">{lang === "it" ? "Esempio" : "Sample"}</span>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
              );
            })}
          </MapContainer>
        </div>

        <div className="mt-6 flex items-center gap-6 text-xs text-sand/60 flex-wrap">
          <span className="inline-flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full bg-primary"></span>
            {lang === "it" ? "Strutture nella rete" : "Structures in the network"}
          </span>
          <span className="inline-flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5 text-sage"/> {lang === "it" ? "Verificata" : "Verified"}
          </span>
          <span className="ml-auto">
            {pins.length} {lang === "it" ? "strutture" : "structures"}
          </span>
        </div>
      </div>
    </section>
  );
}
